import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeviceDetection } from '../../../hooks/UseDeviceDetection';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import useScreenSize from '../../../hooks/UseScreenSize';
import moment from 'moment';
import { useAsyncList } from 'react-stately';
import {
  Flex,
  View,
  ProgressCircle,
  Grid,
  TableView,
  TableBody,
  TableHeader,
  Row,
  Cell,
  Column,
  Heading,
  Content,
  ListBox,
  Item,
  IllustratedMessage,
  Link,
  DialogContainer,
} from '@adobe/react-spectrum';
import { CircleFill } from 'react-bootstrap-icons';
import { FinderIcon } from '../../../components/protected/Icons/IconsLib';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import FilterComponent from './component/FilterComponent';
import HeaderComponent from './component/HeaderComponent';
import { invokeFinderApp } from '../../../utils/NavigationUtils';
import { RootState } from '../../../state/store';
import { ListProjectAdvancedRequest } from '../../../services/soap/project/requests/ListProjectAdvancedRequest';
import { GetFormPanelRequest } from '../../../services/soap/features/requests/GetFormPanelRequest';
import { SoapUtils } from '../../../utils/SoapUtils';
import { ListFormSettingsRequest } from '../../../services/soap/features/requests/ListFormSettingsRequest';
import { GetFormSettingsRequest } from '../../../services/soap/features/requests/GetFormSettingsRequest';
import { ListRegisterRequest } from '../../../services/soap/features/requests/ListRegisterRequest';
import CardComponent from './component/CardComponent';
import CardComponentMobile from './component/CardComponentMobile';
import { ComponentFactory } from '../../../components/protected/layout-components/ComponentFactory';
import { FPAData, FPADataFactory, FPADataTypes } from '../../../infra/protected/FPA/FPAData';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import FilterSearchTextBox from './component/FilterSearchTextBox';
import useNumberFormatter from '../../../i18n/useNumberFormatter';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';
import { DataProvider } from '../../../components/protected/data-components/Contexts/DataContext';
import { ContextNewDialog, ContextNewDialogMode } from '../../../components/protected/Context/ContextNewDialog';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';
import { DeleteConfirmationDialog } from '../finder/components/DeleteConfirmationDialog';
import { DeleteProjectRequest } from '../../../services/soap/project/requests/DeleteProjectRequest';
import { ToastQueue } from '@react-spectrum/toast';
import { ContextMoveDialog } from '../../../components/protected/Context/ContextMoveDialog';
import { subscribe, unsubscribe } from '../../../infra/events/Events';
import { AtollonEvent } from '../../../infra/events/Events';
import { useProjectActivitiesState } from './hooks/useProjectActivitiesState';
import './project_activities.css';
import styles from './project_activities.module.css';

interface Character {
  id: string;
  name: string;
}

export function ProjectActivities() {
  const { t } = useTranslation();
  const screenSize = useScreenSize();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const { activityStatusMapping, currencies, projectStatusMapping } = usePreloadAssets();
  const { store, projectService, featureService } = useDependency();
  const dataStore = useSelector((state: any) => state.userWebLayout.appInfos);
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const listBoxRef = useRef<any>(null);
  // Use custom hook for state management
  const { state: componentState, actions } = useProjectActivitiesState();
  // Destructure state for easier access
  const { 
    showLoader, 
    showLoaderDetail, 
    rowsData, 
    pageView, 
    selectedKeys, 
    definition, 
    showMobileDetailView, 
    selectedItem, 
    selectedDataRow, 
    showAdd, 
    isMoveDialogOpen, 
    isDeleteDialogOpen, 
    showFilter 
  } = componentState;
  const searchedValueRef = useRef<string>('');
  const columnsDataRef = useRef<any>([]);
  const setColumnName = useRef<any>([]);
  const [projectStatusList, setProjectStatusList] = useState<any>([]);
  const [stopLoadData, setStopLoadData] = useState<boolean>(false);
  const projectActivityValue = useRef<string>('');
  const currencyList = useRef<any>([]);
  const queryParams = useRef<any>({ FILTER: { COL: [] } });
  const totalProjectListRec = useRef<number>(0);
  const offSetLimit = useRef<number>(0);
  const dataRef = useRef<any>({});
  const stopLoadDataRef = useRef<boolean>(false);
  const formatNumber = useNumberFormatter();
  const modeRef = useRef<ContextNewDialogMode>('new');
  const previousSelectedAppRef = useRef<string | null>(null);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (state.appKey && dataStore[state.appKey]) {
      if (dataStore[state.appKey].pageView) {
        dataRef.current.pageView = dataStore[state.appKey].pageView;
        actions.setPageView(dataStore[state.appKey].pageView);
        return;
      }
    }
  }, [dataStore.appInfos, state.appKey]);

  
  useKeyboardShortcuts([...addKey('c', { ctrl: true, shift: true }, () => onAddNew())], true);

  const getCurrencyList = useCallback(() => {
    currencyList.current = currencies.map((item: any) => ({
      id: item.id,
      code: item.code
    }));
  }, [currencies]);
  
  const getProjectStatusArray = useCallback(async () => {
    const statusArray: any = [];
    if(projectStatusMapping){
      for(var key in projectStatusMapping as any){
        statusArray.push({ id: key, name: (projectStatusMapping as any)[key].name, color: (projectStatusMapping as any)[key].color });
      }
    }
    statusArray.unshift({ id: 'All states', name: 'All states', color: 'black' });
    setProjectStatusList(statusArray);
  }, [projectStatusMapping]);

  // Main effect for selectedApp changes
  useEffect(() => {
    const initializeForNewApp = async () => {
      // Only proceed if selectedApp has actually changed
      if (previousSelectedAppRef.current === selectedApp) {
        return;
      }
      
      previousSelectedAppRef.current = selectedApp;
      
      actions.setShowLoader(true);
      actions.setShowLoaderDetail(true);
      actions.setPageView('Grid');
      actions.setShowFilter(true);
      queryParams.current = { FILTER: { COL: [] } };
      projectActivityValue.current = '';
      
      // Reset data state when selectedApp changes
      actions.setRowsData([]);
      actions.setSelectedKeys(undefined);
      actions.setSelectedDataRow({});
      actions.setDefinition(null);
      totalProjectListRec.current = 0;
      offSetLimit.current = 0;
      
      getCurrencyList();
      await getProjectStatusArray();
    };
    
    initializeForNewApp();
  }, [selectedApp, getCurrencyList, getProjectStatusArray, actions]);

  // Effect to reload data when all dependencies are ready
  useEffect(() => {
    if (selectedApp && 
        currencyList.current.length > 0 && 
        projectStatusList.length > 0) {
      // Use setTimeout to ensure state updates have been processed
      const timeoutId = setTimeout(() => {
        listProjectList.reload();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp, projectStatusList]);

  useEffect(() => {
    subscribe(AtollonEvent.CREATE_CONTEXT_APP, handleCreateContextApp);

    return () => {
      dispatch(
        updateAppInfo({
          key: state.appKey,
          data: {
            pageView: dataRef.current.pageView,
          },
        })
      );

      unsubscribe(AtollonEvent.CREATE_CONTEXT_APP, handleCreateContextApp);
    };
  }, []);

  const getFormSettings = useCallback(
    async (settingId: string) => {
      //console.log('settingId', settingId);
      if (settingId === '') {
        const resFormSettings = await featureService.listFormSettings(new ListFormSettingsRequest(store.Server, store.SessionId));
        let filteredData = resFormSettings.SETTING.filter((item: any) => item.formId === 'ListProjectAdvanced' && item.isDefault == '1');
        if (filteredData?.length > 0) {
          settingId = filteredData[0].id.toString();
        }
      }

      const response = await featureService.getFormSettings(new GetFormSettingsRequest(store.Server, store.SessionId, settingId));
      if (response?.result === 'OK' && response?.count > 0) {
        const result = response.SETTING[0]?.ITEM?.filter(item => String(item.visibility) === '1').map(item => ({
          key: item.itemname.includes('|') ? item.itemname.split('|')[1] : item.itemname,
          name: item.caption?.trim() ? item.caption : item.name,
        }));
        let columnsName = result.map(item => item.key);
        //console.log('columnsName', columnsName);
        setColumnName.current = columnsName;
        //console.log('result', result);
        //change key value that has . with _ in result
        result.forEach(item => {
          if (item.key.includes('.')) {
            item.key = item.key.replace(/\./g, '_');
          }
        });
        columnsDataRef.current = result;
      }
    },
    [selectedApp]
  );

  const getProjectListKey = useCallback(async () => {
    //console.log('selectedApp', selectedApp);
    let resListRegister = await featureService.listRegister(new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, +selectedApp));
    //console.log('resListRegister', resListRegister);
    projectActivityValue.current = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListProject')?.ROW.value ?? '';

    let settingId = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'formSetting')?.ROW.value ?? '';

    await getFormSettings(settingId);

    return resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListProject')?.ROW.value ?? '';
  }, [selectedApp]);

  const getProjectListCount = async () => {
    //console.log('object', filterParams?.FILTER);
    var projectActivityValue_current = projectActivityValue.current;
    if(!projectActivityValue_current || projectActivityValue_current !== ''){
      projectActivityValue_current = await getProjectListKey();
    }

    const fpaTypeCategoryExists = queryParams.current.FILTER.COL.some((col: any) => col.name === 'fpaTypeCategory');
    if (!fpaTypeCategoryExists) {
      queryParams.current.FILTER.COL.push({
        name: 'fpaTypeCategory',
        type: 'any',
        value: projectActivityValue_current,
      });
    }
    console.log('projectActivityValue_current: ', projectActivityValue_current);

    projectActivityValue.current = projectActivityValue_current;
    // Remove duplicates in the FILTER.COL array
    const uniqueCOL: any = [];
    const typeName = new Set();

    queryParams.current.FILTER.COL.forEach((item: any) => {
      if (!typeName.has(item.name)) {
        typeName.add(item.name);
        uniqueCOL.push(item);
      }
    });

    // Update the COL array with unique values
    queryParams.current.FILTER.COL = uniqueCOL;

    let projectRes = await projectService.listProjectAdvanced(
      new ListProjectAdvancedRequest(store.Server, store.SessionId, {
        countOnly: '1',
        FILTER: queryParams.current?.FILTER,
      })
    );
    if (projectRes?.result == 'OK') {
      totalProjectListRec.current = projectRes.count != 0 ? projectRes.count : 0;
      return projectRes.count != 0 ? projectRes.count : 0;
    }
  };

  const loadProjectActivities = async () => {
    try {
      //console.log('loadProjectActivities Called');
      if (stopLoadData) {
        return rowsData;
      }
      let projectRes = await projectService.listProjectAdvanced(
        new ListProjectAdvancedRequest(store.Server, store.SessionId, {
          limit: PAGE_SIZE,
          offset: offSetLimit.current ?? 0,
          FILTER: queryParams.current?.FILTER,
          //add columnsData keys to the request
          FIELDS: { field: setColumnName.current },
          ORDER: {
            COL: [
              {
                name: 'modified',
                desc: 'true',
              },
            ],
          },
        })
      );
      //console.log('projectRes)', projectRes);
      if (projectRes?.result == 'OK' && projectRes?.count > 0) {
        let listProject: any = projectRes?.PROJECTS?.PROJECT;
        listProject.forEach((item: any) => {
          item.chargeable = item.chargeable === '1' ? 'Yes' : 'No';
          const currency = currencyList.current.find((currency: any) => currency.id == item.currency);
          if (item.amountWithoutVat) {
            item.amountWithoutVat = currency ? currency.code + ' ' + formatNumber(Number(item.amountWithoutVat?.split('.')[0].replace(/\D/g, ''))) : item.amountWithoutVat;
          } else {
            item.amountWithoutVat = 0;
          }
        });
        actions.appendRowsData(listProject);
        //console.log('listProject', listProject);
        if (listProject?.length > 0 && selectedKeys === undefined) {
          actions.setSelectedKeys(new Set([listProject[0].id]));
          actions.setSelectedDataRow(listProject[0]);
          getLayout(listProject[0]);
        }
        return listProject;
      } else {
        actions.setRowsData([]);
        //ToastQueue.info(t('no_results', { ns: 'layout_components' }), { timeout: 50 });
        return [];
      }
    } finally {
      actions.setShowLoader(false);
    }
  };

  const listProjectList = useAsyncList<Character>({
    async load({ cursor, signal }) {
      try {
        // Early return if no app is selected
        if (!selectedApp || !currencyList.current || !projectStatusList) {
          return { items: [], cursor: undefined };
        }

        if (signal?.aborted) {
          //throw new Error('Request was aborted');
          //console.log('Request was aborted');
          return { items: [], cursor: undefined };
        }

        // Check if we've reached the end of available data
        const currentPage = cursor ? parseInt(cursor, 10) : 0;
        const estimatedLoadedCount = currentPage * PAGE_SIZE;
        if (cursor && estimatedLoadedCount >= totalProjectListRec.current) {
          return { items: rowsData, cursor: undefined }; // End of list reached
        }

        // Fetch total count only when needed (first load or filter changes)
        const totalRecords = currentPage === 0 ? await getProjectListCount() : totalProjectListRec.current;

        // Calculate pagination offset with bounds protection
        const offset = Math.min(PAGE_SIZE * currentPage, totalRecords ?? 0);

        // Store for other components' reference
        offSetLimit.current = offset;

        // Load the actual data
        const items = await loadProjectActivities();
        const processedData = processJsonData(items);
        //replace key that has . with _ in processedData
        processedData.forEach((item: any) => {
          Object.keys(item).forEach(key => {
            if (key.includes('.')) {
              item[key.replace(/\./g, '_')] = item[key];
              delete item[key];
            }
          });
        });

        //console.log('processedData', processedData);
        // Calculate next cursor based on remaining items
        const hasMoreItems = offset + (processedData?.length || 0) < (totalRecords ?? 0);
        const nextCursor = hasMoreItems ? String(currentPage + 1) : undefined;

        return {
          items: processedData || [],
          cursor: nextCursor,
        };
      } catch (error) {
        console.error('Error loading activity list:', error);
        ToastQueue.negative(t('error_loading_data', { ns: 'layout_components' }) || 'Error loading data', { timeout: 3000 });
        return { items: [], cursor: undefined };
      }
    },

    async sort({ items, sortDescriptor }) {
      // Return the current items if no sort descriptor is provided
      if (!sortDescriptor) return { items };

      const { column, direction } = sortDescriptor;

      return {
        items: [...items].sort((a: any, b: any) => {
          const aValue = a[column];
          const bValue = b[column];

          // Handle different data types for comparison
          let result = 0;
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            result = aValue.localeCompare(bValue);
          } else if (aValue < bValue) {
            result = -1;
          } else if (aValue > bValue) {
            result = 1;
          }

          return direction === 'descending' ? -result : result;
        }),
      };
    },
    getKey: item => item.id,
  });

  // Process JSON data to transform specific structures i.e. Change Array or Object value to Comma Separated String
  function processJsonData(data: any) {
    return data.map((item: any) => {
      // Create a copy of the item
      const newItem = { ...item };
      // Look for all keys in the object
      Object.keys(newItem).forEach(key => {
        const value = newItem[key];

        // If value is an object (but not null)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const objectKeys = Object.keys(value);

          // Case 1: Object with single array property - convert to comma-separated string
          if (objectKeys.length === 1 && Array.isArray(value[objectKeys[0]])) {
            newItem[key] = value[objectKeys[0]].join(', ');
          }
          // Case 2: Simple object with a single value property - extract just the value
          else if (objectKeys.length === 1 && typeof value[objectKeys[0]] !== 'object') {
            newItem[key] = value[objectKeys[0]];
          }
        }
        // If value is directly an array
        else if (Array.isArray(value)) {
          newItem[key] = value.join(', ');
        }
      });

      return newItem;
    });
  }

  const reloadProjectActivities = useCallback((filterParams: any) => {
    //console.log('reloadProjectActivities Called', filterParams);
    setStopLoadData(false);
    queryParams.current = filterParams;
    totalProjectListRec.current = 0;
    offSetLimit.current = 0;
    listProjectList.reload();
  }, []);

  const handleTableRowdblClick = (rowId: any): void => {
    invokeFinderApp(rowId);
  };

  const handleTableRowClick = (rowId: any): void => {
    actions.setSelectedKeys(rowId);
    let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
    if (!selectedRow) {
      actions.setSelectedDataRow({});
      actions.setSelectedKeys(undefined);
      actions.setDefinition(null);
      return;
    }
    actions.setSelectedDataRow(selectedRow);
    getLayout(selectedRow);
  };

  const handleListRowClick = (rowId: any): void => {
    if (selectedKeys?.has(rowId.currentKey)) {
      return;
      // setSelectedDataRow({});
      // setSelectedKeys(undefined);
      // setDefinition(null);
    } else {
      console.log('rowId', rowId);
      actions.setShowLoaderDetail(true);
      actions.setSelectedKeys(rowId);
      actions.setPageView('Full');
      dataRef.current.pageView = 'Full';
      let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);

      if (!selectedRow) {
        actions.setSelectedDataRow({});
        actions.setSelectedKeys(undefined);
        actions.setDefinition(null);
        return;
      }
      actions.setSelectedDataRow(selectedRow);
      getLayout(selectedRow);
    }
  };
  const scrollPositionRef = useRef<number>(0);
  const handleRowClickMobile = (rowId: any): void => {
    actions.setSelectedKeys(undefined);
    let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
    if (!selectedRow) {
      actions.setSelectedDataRow({});
      actions.setSelectedKeys(undefined);
      actions.setDefinition(null);
      return;
    }
    actions.setSelectedDataRow(selectedRow);
    getLayout(selectedRow);
    actions.setShowMobileDetailView(true);
  };

  const handleViewChange = (view: string) => {
    actions.setPageView(view);
    dataRef.current.pageView = view;
  };

  const getStatusColor = (statusName: any) => {
    const statusObj = projectStatusList.find((obj: any) => obj.name === statusName);
    return (
      <>
        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusName}
      </>
    );
  };

  const addStatusColor = (statusName: any) => {
    const statusObj = projectStatusList.find((obj: any) => obj.name === statusName);
    return statusObj?.color;
  };

  const getLayout = async (selectedRow: any) => {
    try {
      let resSelectedItem = FPADataFactory.createFromProjectAdvanced(selectedRow, activityStatusMapping);
      actions.setSelectedItem(resSelectedItem);
      let typeId = selectedRow.projectType;
      const layout = await featureService.getFormPanel(new GetFormPanelRequest(store.Server, store.SessionId, '' + typeId));

      if (layout.count > 0) {
        let scheme = SoapUtils.parseXmlString2(layout.ROW.SCHEME);
        let layoutObject = JSON.parse(scheme.ActivityPanelSettings.layoutObject);
        actions.setDefinition(layoutObject);
      } else {
        actions.setDefinition(null);
      }
    } catch (err) {
      console.error('getlayout error: ', err);
    } finally {
      actions.setShowLoaderDetail(false);
    }
  };

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex direction="row" width="100%" alignContent="center" justifyContent="center">
      <IllustratedMessage>
        <NotFound />
        <Heading>{t('no_results', { ns: 'layout_components' })}</Heading>
        <Content>{t('try_another_search', { ns: 'layout_components' })}</Content>
      </IllustratedMessage>
    </Flex>
  );

  const getHeight = (baseHeight: number) => {
    return `${baseHeight - 120}px`;
  };

  const getWidth = () => {
    return `95%`;
  };

  const detailStyle = {
    height: getHeight(screenSize.height - 20),
  };

  const cardStyle = {
    height: getHeight(screenSize.height),
    width: getWidth(),
    paddingBottom: '15px',
  };

  const filterStyle = {
    height: getHeight(screenSize.height + 60),
    overflow: 'hidden auto',
  };

  const onAddNew = () => {
    actions.setShowAdd(true);
  };

  const nAddNewClose = (reload_flag: boolean) => {
    if (reload_flag) listProjectList.reload();

    actions.setShowAdd(false);
  };
  const nAddNewCloseDismiss = () => {
    actions.setShowAdd(false);
  };

  const handleCopy = () => {
    if (selectedItem) {
      actions.setShowAdd(true);
    }
  };

  const handleDelete = (item: FPAData | null) => {
    if (item === null) return;
    actions.setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedItem === null) return;
    actions.setIsDeleteDialogOpen(false);
    if (selectedItem.type === FPADataTypes.PROJECT) {
      const res = await projectService.deleteProject(new DeleteProjectRequest(store.Server, store.SessionId, selectedItem.id));
      if (res.EXCEPTION) {
        ToastQueue.negative(res.EXCEPTION.message, { timeout: 3000 });
      } else {
        listProjectList.reload();
      }
    }
  };

  const handleDeleteProject = () => {
    if (selectedItem === null) return;
    handleDelete(selectedItem);
  };

  const handleMoveClose = (moved: boolean) => {
    if (moved) {
      listProjectList.reload();
    }
    actions.setIsMoveDialogOpen(false);
  };
  const handleMove = (_selectedItem: FPAData | null) => {
    if (_selectedItem) {
      actions.setIsMoveDialogOpen(true);
      actions.setSelectedItem(_selectedItem);
    } else {
      ToastQueue.negative(t('no_selected_item', { ns: 'layout_components' }), { timeout: 3000 });
    }
  };
  const handleCreateContextApp = (event: AtollonEvent) => {
    if (event.detail.isCopy) {
      handleCopy();
    } else {
      handleMove(event.detail.selectedItem);
    }
  };

  const scrollToSelected = useCallback(() => {
      setTimeout(() => {
        const listContainer = document.getElementById('listProjectCardsMobile');
        if (listContainer && scrollPositionRef.current > 0) {
          listContainer.scrollTo({
            top: scrollPositionRef.current,
            behavior: 'smooth',
          });
          //console.log('Restored scroll position:', scrollPositionRef.current);
        }
      }, 1000);
    }, []);
  
    // Component system attributes for consistency
    const systemAttributes = {
      onPrint: (item: any) => item,
      onEdit: (item: any) => console.log('onEdit', item),
      onDelete: handleDelete,
      onChat: (item: any) => console.log('onChat', item),
      onConfig: (item: any, type: any) => console.log('onConfig', item, type),
    };
  
    // Handle mobile detail view navigation
    const handleMobileDetailClose = () => {
      stopLoadDataRef.current = true;
      actions.setShowMobileDetailView(false);
      dataRef.current.showMobileDetailView = false;
      scrollToSelected();
    };
  
    // Render mobile list view
    const renderMobileListView = () => {
      // console.log('MOBILE LIST VIEW DEBUG:', {
      //   isLoading: listProjectList.isLoading,
      //   itemsLength: listProjectList.items?.length,
      //   rowsDataLength: rowsData?.length,
      //   selectedKeys,
      //   showMobileDetailView
      // });
      
      return (
        <View 
          width={'100%'} 
          height={'100%'} 
          UNSAFE_style={{ 
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Filter Component */}
          <View UNSAFE_style={{ height: '60px', flexShrink: 0, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <FilterComponent onFilterChange={reloadProjectActivities} filterParams={queryParams.current} />
          </View>
          
          {/* Search Component */}
          <View UNSAFE_style={{ height: '50px', flexShrink: 0, backgroundColor: 'white', padding: '5px 10px', borderBottom: '1px solid #e0e0e0' }}>
            <Flex direction={'row'} height={'40px'} alignItems={'center'} justifyContent={'end'} gap={'8px'}>
              <FilterSearchTextBox
                onSearchChange={reloadProjectActivities}
                filterParams={queryParams.current}
                serachedText={searchedValueRef.current}
                fpaTypeCategory={projectActivityValue.current}
              />
              <i className={`${styles.icon_search} bi bi-search`} />
            </Flex>
          </View>
          
          {/* Main Content Area - List */}
          <View 
            flex={1}
            overflow={'auto'}
            UNSAFE_style={{ 
              padding: '5px',
              backgroundColor: '#f5f6f9'
            }}
          >
            {listProjectList.isLoading ? (
              <View UNSAFE_style={{ padding: '20px' }}>
                <Loader />
              </View>
            ) : (
              <ListBox
                ref={listBoxRef}
                id="listProjectCardsMobile"
                selectionMode="single"
                onSelectionChange={handleRowClickMobile}
                minHeight={'130px'}
                maxHeight={deviceInfo.shouldUseMobileLayout() ? `${deviceInfo.screenHeight}px` : '100%'}
                selectedKeys={selectedKeys}
                aria-label="Project Activities Cards Mobile"
                items={listProjectList.items?.length > 0 ? listProjectList.items : rowsData || []}
                isLoading={listProjectList.isLoading}
                onLoadMore={listProjectList.loadMore}
                UNSAFE_style={{ 
                  width: '100%',
                  backgroundColor: 'transparent'
                }}
              >
                {item => (
                  <Item key={item.id} textValue={item.name}>
                    <CardComponentMobile item={item} projectStatusList={projectStatusList} />
                  </Item>
                )}
              </ListBox>
            )}
          </View>
          
          {/* Add New Button */}
          <View
            position={'absolute'}
            right={'16px'}
            bottom={'95px'}
            zIndex={10}
          >
            
              <Flex
                height={'48px'}
                justifyContent={'center'}
                alignItems={'center'}
                gap={'8px'}
                UNSAFE_className={styles.add_new_parent}
              >
                <Link isQuiet onPress={onAddNew} UNSAFE_className={styles.add_new_btn}>
                <i className="bi bi-plus fs-5">
                  <View UNSAFE_className={styles.add_new_btn_text}>{t('add_new', { ns: 'layout_components' })}</View>
                </i>
                </Link>
              </Flex>
            
          </View>
        </View>
      );
    };  // Render mobile detail view
    const renderMobileDetailView = () => (
      <>
        <View position={'relative'} width={'100%'} height={'64px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_parent}>
          <View position={'absolute'} top={'calc(50% - 14px)'} left={'15%'} UNSAFE_className={styles.layout_header_text}>
            {selectedItem?.item_type_name}
          </View>
          <View position={'absolute'} top={'calc(50% - 15px)'} left={'25px'} width={'24px'} height={'30px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_btn_back}>
            <Link onPress={handleMobileDetailClose}>
              <i className="bi bi-chevron-left fs-5" />
            </Link>
          </View>
          <View position={'absolute'} top={'calc(50% - 15px)'} right={'25px'} width={'24px'} height={'30px'} overflow={'hidden'}>
            <Link
              onPress={() => {
                stopLoadDataRef.current = true;
                actions.setShowMobileDetailView(false);
                dataRef.current.showMobileDetailView = false;
              }}
              UNSAFE_className={styles.layout_header_btn_folder}
            >
              <FinderIcon size={24} />
            </Link>
          </View>
        </View>
        <Flex direction="column" alignItems={'center'} justifyContent={'center'} UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ padding: '20px', paddingTop: '0px' }}>
          {selectedItem && (
            <>
              {showLoader ? (
                <Loader />
              ) : (
                <>
                  <View
                    UNSAFE_className={styles.service_status_bar}
                    marginBottom={'5px'}
                    marginTop={'0px'}
                    UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName) }}
                  />
                  <ComponentFactory
                    key={selectedDataRow.id}
                    selectedItem={selectedItem}
                    definition={definition}
                    systemAttributes={systemAttributes}
                  />
                </>
              )}
            </>
          )}
        </Flex>
      </>
    );
  
    // Force desktop layout for tablet landscape
    const shouldUseDesktopLayout = !deviceInfo.shouldUseMobileLayout() || ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape');
  
    return (
      <div key={0}>
        {!shouldUseDesktopLayout ? (
          <>
            {!showMobileDetailView ? renderMobileListView() : renderMobileDetailView()}
          </>
        ) : (
          // Desktop/Tablet Layout
          <Grid
            areas={
              (deviceInfo.isTablet || deviceInfo.isIPad)
                ? pageView === 'Full' 
                  ? ['header header', 'filter filter', 'card detail'] 
                  : ['header header', 'filter filter', 'detail detail']
                : pageView === 'Full' 
                  ? ['filter header header', 'filter card detail'] 
                  : ['filter header header', 'filter detail detail']
            }
            columns={{ 
              base: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
                ? ['35%', '65%']
                : ['245px', '28%', 'auto'], 
              L: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
                ? ['35%', '65%']
                : ['245px', '28%', 'auto']
            }}
            rows={
              (deviceInfo.isTablet || deviceInfo.isIPad)
                ? ['auto', 'auto', '1fr']
                : ['auto', '1fr']
            }
            height="100%"
            width="100%"
            UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '0px', margin: '0px', overflow: 'hidden' }}
          >
            {/* Filter Area - Hidden on tablet landscape, shown for desktop */}
            {!(deviceInfo.isTablet || deviceInfo.isIPad) && (
              <View gridArea="filter">
                {showFilter ? (
                  <FilterComponent onFilterChange={reloadProjectActivities} filterParams={queryParams.current} filterStyle={filterStyle} />
                ) : (
                  <Flex
                    height={'100%'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    UNSAFE_style={{ backgroundColor: 'white' }}
                  >
                    <Loader />
                  </Flex>
                )}
              </View>
            )}
  
            {/* Header Area */}
            <View width={'100%'} gridArea="header" UNSAFE_style={pageView === 'Full' ? {} : { height: '100%' }}>
              <HeaderComponent
                onPageViewChange={handleViewChange}
                autoChanged={pageView}
                filterParams={queryParams.current}
                onSearchChange={reloadProjectActivities}
                fpaTypeCategory={projectActivityValue.current}
                onAddNew={onAddNew}
                onCopy={handleCopy}
                onDelete={handleDeleteProject}
              />
            </View>
  
            {/* Mobile Filter Area - Shown for tablets/iPads under header */}
            {(deviceInfo.isTablet || deviceInfo.isIPad) && (
              <View gridArea="filter" UNSAFE_style={{ height: '60px', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
                <FilterComponent onFilterChange={reloadProjectActivities} filterParams={queryParams.current} />
              </View>
            )}
  
            {pageView === 'Full' ? (
              <>
                {showLoader ? (
                  <Flex
                    width={'100%'}
                    height={'100%'}
                    position={'absolute'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    UNSAFE_style={{ backgroundColor: 'white' }}
                  >
                    <Loader />
                  </Flex>
                ) : (
                  <>
                    {/* Card List Area */}
                    <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9' }}>
                      {rowsData?.length > 0 ? (
                        <ListBox
                          autoFocus
                          id="listProjectCards"
                          selectionMode="single"
                          onSelectionChange={handleListRowClick}
                          marginStart={'15px'}
                          marginTop={'13px'}
                          minHeight={'130px'}
                          maxHeight={'100vh'}
                          selectedKeys={selectedKeys}
                          aria-label="Project Activities Cards"
                          items={listProjectList.items}
                          isLoading={listProjectList.isLoading}
                          onLoadMore={listProjectList.loadMore}
                          UNSAFE_style={cardStyle}
                        >
                          {item => (
                            <Item key={item.id} textValue={item.name}>
                              <CardComponent item={item} projectStatusList={projectStatusList} />
                            </Item>
                          )}
                        </ListBox>
                      ) : (
                        <View marginTop={'13px'}>
                          <NoResultsMessage />
                        </View>
                      )}
                    </View>
  
                    {/* Detail Area */}
                    <View
                      gridArea="detail"
                      backgroundColor={'static-white'}
                      borderRadius={'large'}
                      padding={'size-100'}
                      paddingTop={'0'}
                      marginTop={'17px'}
                      width={'98%'}
                      UNSAFE_style={detailStyle}
                      UNSAFE_className={styles.detailView}
                    >
                      {definition && (
                        showLoaderDetail ? (
                          <Loader />
                        ) : (
                          <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                            {selectedItem && rowsData?.length > 0 && (
                              <>
                                <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName) }} />
                                <View height={'2px'}>&nbsp;</View>
                                <ComponentFactory
                                  selectedItem={selectedItem}
                                  definition={definition}
                                  systemAttributes={systemAttributes}
                                />
                              </>
                            )}
                          </View>
                        )
                      )}
                    </View>
                  </>
                )}
              </>
            ) : (
              // Table View for Detail-only mode
              <View gridArea="detail">
                {showLoader ? (
                  <Loader />
                ) : (
                  <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                    {columnsDataRef?.current.length > 0 && rowsData?.length > 0 ? (
                      <TableView
                        UNSAFE_className="tblLayoutComponent"
                        aria-label="Project Activities"
                        onSelectionChange={handleTableRowClick}
                        selectionMode="single"
                        selectedKeys={selectedKeys}
                        selectionStyle="highlight"
                        width={{ base: '100%', L: '100%', M: '1100px' }}
                        minHeight={'200px'}
                        maxHeight={{ base: '100vh', L: '625px' }}
                        onAction={handleTableRowdblClick}
                      >
                        <TableHeader columns={columnsDataRef.current}>
                          {(column: any) => (
                            <Column showDivider key={column.key}>
                              {column.name}
                            </Column>
                          )}
                        </TableHeader>
                        <TableBody items={listProjectList.items} loadingState={listProjectList.loadingState} onLoadMore={listProjectList.loadMore}>
                          {(item: any) => (
                            <Row>
                              {columnKey => (
                                <Cell>
                                  {(() => {
                                    if (moment(item[columnKey], moment.ISO_8601, true).isValid()) {
                                      return moment(item[columnKey]).format('MM/DD/YYYY | hh:mm A');
                                    } else if (columnKey === 'customStateName') {
                                      return getStatusColor(item[columnKey]);
                                    } else {
                                      return item[columnKey];
                                    }
                                  })()}
                                </Cell>
                              )}
                            </Row>
                          )}
                        </TableBody>
                      </TableView>
                    ) : (
                      <NoResultsMessage />
                    )}
                  </View>
                )}
              </View>
            )}
          </Grid>
        )}
  
        {/* Dialog Components */}
        <DataProvider>
          <DialogContainer onDismiss={nAddNewCloseDismiss}>
            {showAdd && (
              <ContextNewDialog
                parentItem={selectedItem}
                onClose={nAddNewClose}
                additionalMode={true}
                additionalData={{ fpaTypeCategory: projectActivityValue.current, fpaType: 'PROJECT', haveMasterActivity: false }}
                mode={modeRef.current}
              />
            )}
            {isMoveDialogOpen && (
              <ContextMoveDialog
                onClose={handleMoveClose}
                parentItem={null}
                selectedItem={selectedItem}
                showActivities={false}
                showProjects={true}
                showFolders={true}
                relatedToActivityType={selectedItem?.item_type_id}
                selectedItemType={FPADataTypes.PROJECT}
              />
            )}
          </DialogContainer>
        </DataProvider>
        
        <DeleteConfirmationDialog 
          isOpen={isDeleteDialogOpen} 
          onClose={() => actions.setIsDeleteDialogOpen(false)} 
          onConfirm={handleConfirmDelete} 
          itemName={selectedItem?.title} 
        />
      </div>
    );
}
